// The single seam to the backend. Everything the UI knows about the server goes
// through here, so auth handling and error shape live in exactly one place.
//
// Auth model (managed auth): identity is a Microsoft Entra ID token held by MSAL.
// Every request carries it in the `X-Auth-Token` header (SWA strips Authorization
// on managed functions, so a custom header is required). `/api/*` is anonymous at
// the SWA layer and each function validates the token itself. A 401 means "not
// signed in" → the app shows the Landing, which prompts an MSAL sign-in. `GET
// /api/me` returning { claimed: false } is a 200, not a 401 (handled by the caller).

import { getIdToken, hasServerSession, markServerSession, clearServerSession, refreshSession } from './msal';
import type {
  AdminRuleset,
  Ballot,
  Boards,
  ChatMessage,
  CurrentRound,
  MediaType,
  LeagueInfo,
  Me,
  MeProfile,
  Pace,
  Preset,
  Proposal,
  Results,
  RosterMember,
  RosterProfile,
  SeasonRound,
  SpotifyResult,
  Style,
  TracksResponse,
} from './types';

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

// ---- Session-lost signal --------------------------------------------------
// A live session can lapse mid-use (the MSAL token stops refreshing silently, or
// the Entra session ends). The next call then 401s while the page still shows
// stale, already-loaded data. request() fires this so the app can re-boot back to
// the sign-in screen instead of stranding the user on failing writes. Re-booting
// re-checks getMe, so it's self-correcting: a one-off 401 that isn't really a
// signed-out state just reloads and carries on.
let authLostHandler: (() => void) | null = null;
export function onAuthLost(cb: () => void): void {
  authLostHandler = cb;
}

export type Provider = 'aad' | 'google';

const PROVIDER_KEY = 'tpiap:provider';

export function rememberedProvider(): Provider | null {
  try {
    const v = localStorage.getItem(PROVIDER_KEY);
    return v === 'aad' || v === 'google' ? v : null;
  } catch {
    return null;
  }
}

export function login(provider: Provider = 'aad'): void {
  // Remember the choice so a returning player whose session has lapsed is
  // re-authenticated silently (their Google/Microsoft is still signed in on the
  // device) instead of being shown the landing again.
  try {
    localStorage.setItem(PROVIDER_KEY, provider);
  } catch {
    /* ignore */
  }
  const redirect = encodeURIComponent(location.pathname + location.hash);
  location.href = `/.auth/login/${provider}?post_login_redirect_uri=${redirect}`;
}

export function logout(): void {
  location.href = '/.auth/logout?post_logout_redirect_uri=/';
}

// ---- Active league --------------------------------------------------------
// Which league the app is currently scoped to. Sent as X-League-Id on every
// request (the backend re-checks the caller's membership + role — the header
// only says WHICH league, never authorizes). Set when the player enters a
// league from the hub; persisted so a reload stays in the same league.
const LEAGUE_KEY = 'tpiap:league';

export function getActiveLeagueId(): number | null {
  try {
    const v = localStorage.getItem(LEAGUE_KEY);
    return v ? Number(v) || null : null;
  } catch {
    return null;
  }
}
export function setActiveLeagueId(id: number): void {
  try {
    localStorage.setItem(LEAGUE_KEY, String(id));
  } catch {
    /* ignore */
  }
  clearLeagueCache(); // the new league has its own season_id + allowed-sets
}
export function clearActiveLeague(): void {
  try {
    localStorage.removeItem(LEAGUE_KEY);
  } catch {
    /* ignore */
  }
  clearLeagueCache();
}

// Cold-start / warming statuses to ride out. Deploying restarts the Functions host
// (~30-90s) where /api/* briefly 5xx's; retrying transparently means a deploy no
// longer bounces an active user. NOT retried: 4xx (real client errors) except 401,
// which gets ONE cookie re-establish (below).
const RETRY_STATUS = new Set([500, 502, 503, 504]);
const MAX_ATTEMPTS = 4; // ~ up to 4 tries over ~2.6s of backoff — covers a cold start
const backoffMs = (n: number) => Math.min(1500, 250 * 2 ** n); // 250, 500, 1000, 1500
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // The cookie (once established) rides every same-origin request and the backend
  // authenticates from it — so we SKIP getIdToken() (acquireTokenSilent → ciamlogin.com,
  // the call privacy/wallet extensions block). We only reach for the MSAL token to
  // bootstrap the cookie or as a fallback.
  const league = getActiveLeagueId();
  let reEstablished = false; // at most one cookie re-mint per request

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    let token: string | null = null;
    if (!hasServerSession()) {
      try { token = await getIdToken(); } catch { token = null; }
    }
    const headers: Record<string, string> = { accept: 'application/json', ...(init?.headers as Record<string, string> | undefined) };
    if (token) headers['X-Auth-Token'] = token;
    if (league != null) headers['X-League-Id'] = String(league);

    let res: Response;
    try {
      res = await fetch(path, { cache: 'no-store', credentials: 'same-origin', ...init, headers });
    } catch {
      // Network blip / server not reachable (also happens mid-restart) — retry, then fail.
      if (attempt < MAX_ATTEMPTS - 1) { await delay(backoffMs(attempt)); continue; }
      throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.');
    }

    // Server warming after a deploy → back off and retry rather than surfacing an error.
    if (RETRY_STATUS.has(res.status) && attempt < MAX_ATTEMPTS - 1) {
      await delay(backoffMs(attempt));
      continue;
    }

    if (res.status === 401) {
      // Don't treat the FIRST 401 as signed-out — the cookie may have just expired or
      // been transiently rejected while the API warms. Try ONCE to re-mint the cookie
      // from a token we already hold (no window), then retry. Only give up if that
      // fails (genuinely signed out) or a retried request still 401s.
      if (!reEstablished && (await refreshSession())) {
        reEstablished = true;
        continue;
      }
      clearServerSession();
      authLostHandler?.();
      throw new ApiError(401, 'unauthenticated', 'Your session has ended — please sign in again.');
    }

    const text = await res.text();
    const body = text ? safeJson(text) : null;
    if (!res.ok) {
      const code = (body && body.error) || 'error';
      const message = (body && body.message) || `Request failed (${res.status}).`;
      throw new ApiError(res.status, code, message);
    }
    return body as T;
  }
  // Exhausted retries on 5xx/network — the API is still warming.
  throw new ApiError(503, 'unavailable', 'The server is starting up — try again in a moment.');
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function jsonInit(method: string, payload: unknown): RequestInit {
  return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) };
}

// ---- Leagues (multi-tenant) ----------------------------------------------
// The leagues the caller belongs to, plus create/join. All league-scoped calls
// then carry X-League-Id (see the active-league store above).

export interface MyLeague {
  league_id: number;
  name: string;
  role: 'owner' | 'member';
  player_count: number;
  status: { phase: string | null; round_no: number | null; paused: boolean };
}

// Global account profile (display name / avatar), shared across every league.
export interface Account {
  account_id: number;
  email: string;
  display_name: string;
  avatar_url: string | null;
}
export const getAccountMe = () => request<{ account: Account; leagues: unknown[] }>('/api/account/me');
// PUT mirrors display_name to the caller's per-league users rows. avatar_url is a
// small square image data URI (the client resizes) or null to clear.
export const putAccountProfile = (patch: { display_name?: string; avatar_url?: string | null }) =>
  request<{ display_name: string; avatar_url: string | null }>('/api/account/profile', jsonInit('PUT', patch));

// Passwordless email sign-in — emails a one-time link. Never touches Microsoft, so
// no browser extension can block it. Clicking the link sets the session cookie.
export const requestMagicLink = (email: string) =>
  request<{ ok: boolean }>('/api/auth/magic/request', jsonInit('POST', { email }));

export const getMyLeagues = () => request<MyLeague[]>('/api/my/leagues');

// ---- League settings (owner control panel) --------------------------------
// GET/PUT the ACTIVE league's settings (scoped by X-League-Id, owner-only). PUT
// takes any subset; the changes are wired to real behaviour server-side.

export interface LeagueSettings {
  league_id: number;
  name: string;
  join_code: string;
  house_style: string;
  is_active: boolean;
  league_type: 'dj' | 'jukebox';
  chat_enabled: boolean;
  future_rounds_visible: boolean;
  max_participants: number | null;
  allowed_paces: Pace[];
  player_count: number;
}
export interface LeaguePatch {
  name?: string;
  house_style?: string;
  league_type?: 'dj' | 'jukebox';
  chat_enabled?: boolean;
  future_rounds_visible?: boolean;
  max_participants?: number | null;
  allowed_paces?: Pace[];
  // Which media rounds may use — a non-empty subset of audio/video/image. In Jukebox
  // it constrains what members can propose; also the league's default media menu.
  allowed_media?: MediaType[];
  // League on/off. false = paused: everyone but the owner sees the holding page
  // (getMyLeagues reports status.paused = !is_active). Stamps paused_at server-side.
  is_active?: boolean;
  regenerate_join_code?: boolean;
}
export const getMgmtLeague = () => request<LeagueSettings>('/api/mgmt/league');
// Member-facing league info (any claimed player): season_id + the allowed-sets a
// Jukebox proposal must respect, league_type, and whether the caller owns this league.
export const getLeague = () => request<LeagueInfo>('/api/league');

// Many screens need the active league's season_id (to stop hardcoding SEASON_ID) and
// its allowed-sets. Cache one in-flight fetch per active league so navigating between
// screens doesn't refetch; invalidated when the active league changes.
let leagueCache: { key: number | null; p: Promise<LeagueInfo> } | null = null;
export function cachedLeague(): Promise<LeagueInfo> {
  const key = getActiveLeagueId();
  if (!leagueCache || leagueCache.key !== key) leagueCache = { key, p: getLeague() };
  return leagueCache.p;
}
export function clearLeagueCache(): void { leagueCache = null; }
export const putMgmtLeague = (patch: LeaguePatch) =>
  request<LeagueSettings>('/api/mgmt/league', jsonInit('PUT', patch));
// Pass league_type at creation: the backend seeds the first SEASON from it (Jukebox →
// proposals open + a per-member cap). Setting it only via a later PUT leaves the
// already-seeded season closed — the "Proposals are closed" bug.
export const createLeague = (name: string, league_type?: 'dj' | 'jukebox') =>
  request<MyLeague & { join_code: string }>('/api/leagues', jsonInit('POST', league_type ? { name, league_type } : { name }));
export const joinLeague = (join_code: string) =>
  request<MyLeague>('/api/leagues/join', jsonInit('POST', { join_code }));
// Leave a league (members only; the backend soft-leaves, keeping history, and 409s
// an owner — they delete/transfer instead).
export const leaveLeague = (league_id: number) =>
  request<{ left: boolean }>('/api/leagues/leave', jsonInit('POST', { league_id }));
// Delete a league (owner only; 403 for a non-owner member). FK-safe cascade server-side.
// Clear the active-league pointer on success.
export const deleteLeague = (league_id: number) =>
  request<{ deleted: boolean; league_id: number }>(`/api/leagues/${league_id}`, { method: 'DELETE' });

// ---- Identity -------------------------------------------------------------

export const getMe = async (): Promise<Me> => {
  const me = await request<Me>('/api/me');
  markServerSession(); // a 200 from /api/me confirms auth (cookie or token) → stay on the cookie-only path (esp. magic-link users with no MSAL account)
  return me;
};
export const getUnclaimed = () => request<RosterProfile[]>('/api/roster/unclaimed');
// GET /api/roster (backend PR #5) — active players with names + sprites.
export const getRoster = () => request<RosterMember[]>('/api/roster');
// Roster for a SPECIFIC league (for the hub's avatar stacks), overriding the active
// league. A lightweight cookie-auth GET (the hub is already signed in); best-effort —
// returns [] on any failure so a card just shows no avatars. NOTE: fetches full roster
// incl. avatar data-URIs per league; if the hub ever feels heavy, ask the backend to
// add a compact `members` list to GET /api/my/leagues instead.
export async function getLeagueRoster(leagueId: number): Promise<RosterMember[]> {
  try {
    const res = await fetch('/api/roster', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { accept: 'application/json', 'X-League-Id': String(leagueId) },
    });
    return res.ok ? ((await res.json()) as RosterMember[]) : [];
  } catch {
    return [];
  }
}
export const claim = (join_code: string, user_id: number) =>
  request<unknown>('/api/claim', jsonInit('POST', { join_code, user_id }));
export const putStyle = (style: Style) =>
  request<unknown>('/api/me/style', jsonInit('PUT', { style }));
// Read side of PUT /api/me/profile (backend PR #2). Reaches the frontend branch
// once `main` is merged in; call sites treat a 404 as "not available yet".
export const getProfile = () => request<MeProfile>('/api/me/profile');
export const putProfile = (payload: {
  bio?: string;
  phrases?: string[];
  sprite_data?: string[];
}) => request<unknown>('/api/me/profile', jsonInit('PUT', payload));

// ---- Rounds ---------------------------------------------------------------

export const getCurrentRound = () => request<CurrentRound | null>('/api/rounds/current');
export const getSeasonRounds = (seasonId: number) =>
  request<SeasonRound[]>(`/api/rounds?season_id=${seasonId}`);
export const getTracks = (roundId: number) =>
  request<TracksResponse>(`/api/rounds/${roundId}/tracks`);
export const postSubmission = (roundId: number, media_ref: string, prediction_ref?: string) =>
  request<unknown>(
    `/api/rounds/${roundId}/submission`,
    jsonInit('POST', prediction_ref ? { media_ref, prediction_ref } : { media_ref }),
  );

// ---- Ballots --------------------------------------------------------------

export const getBallot = (roundId: number) => request<Ballot>(`/api/rounds/${roundId}/ballot`);
export const putBallot = (
  roundId: number,
  entries: Ballot['entries'],
  submit: boolean,
) => request<unknown>(`/api/rounds/${roundId}/ballot`, jsonInit('PUT', { submit, entries }));

// ---- Results & boards -----------------------------------------------------

export const getResults = (roundId: number) => request<Results>(`/api/rounds/${roundId}/results`);
export const getBoards = (seasonId: number) => request<Boards>(`/api/boards?season_id=${seasonId}`);

// ---- Chat -----------------------------------------------------------------

export const getMessages = (before?: number, limit = 50) => {
  const p = new URLSearchParams();
  if (before) p.set('before', String(before));
  p.set('limit', String(limit));
  return request<ChatMessage[]>(`/api/messages?${p.toString()}`);
};
export const postMessage = (body: string) =>
  request<ChatMessage>('/api/messages', jsonInit('POST', { body }));
export const deleteMessage = (id: number) =>
  request<unknown>(`/api/messages/${id}`, { method: 'DELETE' });

// @-mention unread badge (backend PR #23/#24). Count is deduped per message and
// excludes deleted messages; POST seen sets the watermark to now.
export const getMentionsUnread = () => request<{ count: number }>('/api/me/mentions/unread-count');
export const postMentionsSeen = () => request<{ ok: boolean }>('/api/me/mentions/seen', { method: 'POST' });

// Editable scoring ruleset (admin). GET the effective config; PUT any subset —
// the response is the new effective config (source of truth after a save).
// League on/off. GET /api/status is anonymous (called before login, on every
// visit) and leaks nothing but { active }. The admin PUT flips it.
export const getStatus = () => request<{ active: boolean }>('/api/status');
export const putActive = (active: boolean) =>
  request<{ active: boolean }>('/api/admin/settings/active', jsonInit('PUT', { active }));

export const getRuleset = () => request<AdminRuleset>('/api/mgmt/ruleset');
export const putRuleset = (input: Partial<AdminRuleset>) =>
  request<AdminRuleset>('/api/mgmt/ruleset', jsonInit('PUT', input));

// ---- Jukebox: presets, proposals, draw ------------------------------------
// Member-submitted rounds (build/member-rounds.md). Presets are admin-managed
// named rulesets members pick from; a proposal is a PROPOSED round the draw later
// shuffles into the running order. Proposal reads/writes target the newest season
// server-side (no season_id needed); the draw takes the season id explicitly.

export type PresetInput = {
  name: string;
  budget: number; track_min: number; track_max: number; negative_multiplier: number;
  veto_threshold_pct: number; veto_penalty: number; popularity_bonus: number; popularity_tie_bonus: number;
  veto_enabled: boolean;
};
export const getPresets = () => request<Preset[]>('/api/presets');
export const createPreset = (input: PresetInput) =>
  request<{ ruleset_id: number }>('/api/mgmt/presets', jsonInit('POST', input));
export const updatePreset = (id: number, patch: Partial<PresetInput>) =>
  request<Preset>(`/api/mgmt/presets/${id}`, jsonInit('PUT', patch));
export const deletePreset = (id: number) =>
  request<unknown>(`/api/mgmt/presets/${id}`, { method: 'DELETE' });

export interface ProposeInput {
  category: string;
  note?: string; // optional free-text description / sub-category (≤140)
  media_type: MediaType;
  preset_id: number;
  veto: boolean;
  pace?: Pace;
}
export const getProposals = () => request<Proposal[]>('/api/rounds/proposals');
export const proposeRound = (input: ProposeInput) =>
  request<{ round_id: number }>('/api/rounds/propose', jsonInit('POST', input));
export const updateProposal = (id: number, patch: Partial<ProposeInput>) =>
  request<{ ok: boolean }>(`/api/rounds/propose/${id}`, jsonInit('PUT', patch));
export const withdrawProposal = (id: number) =>
  request<{ withdrawn: boolean }>(`/api/rounds/propose/${id}`, { method: 'DELETE' });

// Admin season controls for the draw. proposals window (open/deadline/cap) + the
// draw itself (shuffle PROPOSED rounds into a random running order).
export const drawSeason = (seasonId: number) =>
  request<{ drawn: number }>(`/api/mgmt/seasons/${seasonId}/draw`, { method: 'POST' });
export const setProposalWindow = (seasonId: number, patch: { proposals_open?: boolean; proposals_deadline?: string | null; proposals_per_member?: number }) =>
  request<{ ok: boolean }>(`/api/mgmt/seasons/${seasonId}`, jsonInit('PUT', patch));

// ---- Spotify --------------------------------------------------------------

export const spotifySearch = (q: string) =>
  request<SpotifyResult[]>(`/api/spotify/search?q=${encodeURIComponent(q)}`);

// ---- Admin (all require is_admin; 403 otherwise) --------------------------

export interface CreateRoundInput {
  theme: string;
  theme_note?: string;
  media_type?: MediaType;
  is_exhibition?: boolean;
  expected_players?: number;
  veto_threshold?: number;
  pace?: Pace;
}
export const adminCreateRound = (input: CreateRoundInput) =>
  request<{ round_id: number; sequence_no: number; opened: boolean }>(
    '/api/mgmt/rounds',
    jsonInit('POST', input),
  );
export const adminCloseSubmissions = (roundId: number) =>
  request<unknown>(`/api/mgmt/rounds/${roundId}/close-submissions`, { method: 'POST' });
export const adminCloseRound = (roundId: number) =>
  request<unknown>(`/api/mgmt/rounds/${roundId}/close`, { method: 'POST' });
export const adminRescore = (roundId: number) =>
  request<unknown>(`/api/mgmt/rounds/${roundId}/rescore`, { method: 'POST' });
// SCHEDULED → SUBMITTING (409 already_open if another round is live).
export const adminOpenRound = (roundId: number) =>
  request<unknown>(`/api/mgmt/rounds/${roundId}/open`, { method: 'POST' });
// SUBMITTING → SCHEDULED, for an accidental open (409 has_submissions if any entries).
export const adminUnopenRound = (roundId: number) =>
  request<unknown>(`/api/mgmt/rounds/${roundId}/unopen`, { method: 'POST' });
export const adminDeleteRound = (roundId: number) =>
  request<unknown>(`/api/mgmt/rounds/${roundId}`, { method: 'DELETE' });
export const adminReorderRound = (roundId: number, direction: 'up' | 'down') =>
  request<unknown>(`/api/mgmt/rounds/${roundId}/reorder`, jsonInit('POST', { direction }));

// Exhibition-only (409 exhibition_only on a live round).
export const adminExhibitionSubmission = (
  roundId: number,
  user_id: number,
  media_ref: string,
  prediction_ref?: string,
) =>
  request<unknown>(
    `/api/mgmt/rounds/${roundId}/submission`,
    jsonInit('POST', prediction_ref ? { user_id, media_ref, prediction_ref } : { user_id, media_ref }),
  );
export const adminExhibitionBallot = (
  roundId: number,
  user_id: number,
  entries: Ballot['entries'],
  submit: boolean,
) => request<unknown>(`/api/mgmt/rounds/${roundId}/ballot/${user_id}`, jsonInit('PUT', { submit, entries }));

// ---- Spotify (admin) ------------------------------------------------------

export const spotifyStatus = () => request<{ connected: boolean }>('/api/mgmt/spotify/status');
export const adminBuildPlaylist = (roundId: number) =>
  request<{ playlist_id: string; url: string; tracks: number }>(
    `/api/mgmt/rounds/${roundId}/playlist`,
    { method: 'POST' },
  );
/** Connect a Spotify account. Routes through Google login first so the admin's
 *  Google identity carries (a cold hit to /connect mis-defaults to Microsoft). */
export function spotifyConnect(): void {
  location.href = '/.auth/login/google?post_login_redirect_uri=' + encodeURIComponent('/api/mgmt/spotify/connect');
}

export const adminUpdateSettings = (input: { join_code?: string; house_style?: Style }) =>
  request<unknown>('/api/mgmt/settings', jsonInit('PUT', input));
export const adminUnbindUser = (userId: number) =>
  request<unknown>(`/api/mgmt/users/${userId}/unbind`, { method: 'POST' });

// GET /api/mgmt/users — the roster WITH binding info for the People tab.
export interface AdminUser {
  user_id: number;
  display_name: string;
  handle: string;
  is_admin: boolean;
  claimed: boolean;
  claimed_at: string | null;
}
export const adminUsers = () => request<AdminUser[]>('/api/mgmt/users');

// ---- Platform owner (super-admin) -----------------------------------------
// The app-owner console (/owner). Every endpoint here is owner-gated server-side
// (403 for any non-owner, even league admins). is_platform_owner on /api/me is the
// gate the UI reads; these endpoints re-check it. See build/handoff-owner-console.md.

export interface OwnerTotals { leagues: number; members: number; active_rounds: number }
export interface OwnerOverview { totals: OwnerTotals }
export const getOwnerOverview = () => request<OwnerOverview>('/api/owner/overview');

export interface OwnerLeague {
  league_id: number;
  name: string;
  league_type: 'dj' | 'jukebox';
  member_count: number;
  owner_display_name: string | null;
  created_at: string | null; // ISO
  paused: boolean;
}
export const getOwnerLeagues = () => request<OwnerLeague[]>('/api/owner/leagues');

export interface OwnerMember {
  user_id: number;
  display_name: string;
  email: string | null;
  avatar_url: string | null;
  league_count: number;
  created_at: string | null; // ISO — when the account was first seen
}
export const getOwnerMembers = () => request<OwnerMember[]>('/api/owner/members');

// Global (house) defaults — the values new leagues inherit. Owner-editable so the
// app-wide pace timings and starter scoring can change without a redeploy.
export interface HousePace {
  key: Pace;
  label: string;
  submit_hours: number; // hours a submission phase stays open
  vote_hours: number; // hours a voting phase stays open
  enabled: boolean; // offered as a choice when creating a league
}
export interface HouseScoringTemplate {
  key: string;
  name: string;
  veto_enabled: boolean;
  budget: number;
  track_min: number;
  track_max: number;
  negative_multiplier: number;
  veto_threshold_pct: number;
  veto_penalty: number;
  popularity_bonus: number;
  popularity_tie_bonus: number;
}
export interface HouseSettings {
  paces: HousePace[];
  scoring_templates: HouseScoringTemplate[];
}
export const getHouseSettings = () => request<HouseSettings>('/api/owner/settings');
export const putHouseSettings = (patch: Partial<HouseSettings>) =>
  request<HouseSettings>('/api/owner/settings', jsonInit('PUT', patch));
